import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multiselect';
import { Switch } from '@/components/ui/switch';
import { Filter, RotateCcw, Calendar } from 'lucide-react';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
export type { WipFilters } from './wip-filter-types';
export { DEFAULT_WIP_FILTERS, countActiveWipFilters } from './wip-filter-types';
import type { WipFilters } from './wip-filter-types';
import { DEFAULT_WIP_FILTERS, countActiveWipFilters } from './wip-filter-types';

// ─── Props ────────────────────────────────────────────────────────────────────

interface WipFilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: WipFilters;
  onApply: (filters: WipFilters) => void;
  availableResources: string[];
  writtenOffCount: number;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function WipFilterSheet({
  open,
  onOpenChange,
  filters,
  onApply,
  availableResources,
  writtenOffCount,
}: WipFilterSheetProps) {
  // Draft state — only committed on Apply
  const [draft, setDraft] = useState<WipFilters>(filters);

  // Sync draft when sheet opens
  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  const resourceOptions = availableResources.map((r) => ({ label: r, value: r.toLowerCase() }));

  function handleApply() {
    onApply(draft);
    onOpenChange(false);
  }

  function handleClear() {
    const cleared = { ...DEFAULT_WIP_FILTERS };
    setDraft(cleared);
    onApply(cleared);
  }

  const activeCount = countActiveWipFilters(draft);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[360px] sm:w-[400px] flex flex-col p-0" side="right">
        {/* Header */}
        <SheetHeader className="px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <SheetTitle className="text-base">Filters</SheetTitle>
            {activeCount > 0 && (
              <Badge variant="secondary" className="ml-auto text-xs">
                {activeCount} active
              </Badge>
            )}
          </div>
        </SheetHeader>

        {/* Scrollable body */}
        <ScrollArea className="flex-1">
          <ScrollAreaViewport>
            <div className="px-6 py-5 space-y-6">

              {/* ── Group By ─────────────────────────────────────── */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Group By</Label>
                <Select
                  value={draft.groupBy}
                  onValueChange={(v) => setDraft((d) => ({ ...d, groupBy: v }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select grouping..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No grouping</SelectItem>
                    <SelectItem value="resource">Resource / Employee</SelectItem>
                    <SelectItem value="task">Task</SelectItem>
                    <SelectItem value="date">Date</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* ── Date Range ───────────────────────────────────── */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Date Range</Label>
                <div className="grid grid-cols-2 gap-2">
                  {/* From */}
                  <div className="space-y-1">
                    <span className="text-xs text-muted-foreground">From</span>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal text-sm h-9"
                        >
                          <Calendar className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                          {draft.dateFrom
                            ? draft.dateFrom.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })
                            : <span className="text-muted-foreground">Any</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={draft.dateFrom}
                          onSelect={(d) => setDraft((prev) => ({ ...prev, dateFrom: d }))}
                          initialFocus
                        />
                        {draft.dateFrom && (
                          <div className="p-2 border-t">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full text-xs"
                              onClick={() => setDraft((prev) => ({ ...prev, dateFrom: undefined }))}
                            >
                              Clear
                            </Button>
                          </div>
                        )}
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* To */}
                  <div className="space-y-1">
                    <span className="text-xs text-muted-foreground">To</span>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal text-sm h-9"
                        >
                          <Calendar className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                          {draft.dateTo
                            ? draft.dateTo.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })
                            : <span className="text-muted-foreground">Any</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={draft.dateTo}
                          onSelect={(d) => setDraft((prev) => ({ ...prev, dateTo: d }))}
                          initialFocus
                        />
                        {draft.dateTo && (
                          <div className="p-2 border-t">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full text-xs"
                              onClick={() => setDraft((prev) => ({ ...prev, dateTo: undefined }))}
                            >
                              Clear
                            </Button>
                          </div>
                        )}
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>

              <Separator />

              {/* ── Entry Type ───────────────────────────────────── */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Entry Type</Label>
                <Select
                  value={draft.type}
                  onValueChange={(v) => setDraft((d) => ({ ...d, type: v }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-types">All Types</SelectItem>
                    <SelectItem value="time">Time entries only</SelectItem>
                    <SelectItem value="expense">Expenses only</SelectItem>
                    <SelectItem value="invoice">Invoices only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* ── Employee / Resource ──────────────────────────── */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Employee / Resource</Label>
                <MultiSelect
                  options={resourceOptions}
                  value={draft.resources}
                  onValueChange={(vals: string[]) => setDraft((d) => ({ ...d, resources: vals }))}
                  placeholder="All employees..."
                />
                {draft.resources.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs h-7 px-2 text-muted-foreground"
                    onClick={() => setDraft((d) => ({ ...d, resources: [] }))}
                  >
                    Clear selection
                  </Button>
                )}
              </div>

              {/* ── Written Off ──────────────────────────────────── */}
              {writtenOffCount > 0 && (
                <>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-sm font-medium">Show written-off items</Label>
                      <p className="text-xs text-muted-foreground">
                        {writtenOffCount} item{writtenOffCount !== 1 ? 's' : ''} written off
                      </p>
                    </div>
                    <Switch
                      checked={draft.showWrittenOff}
                      onCheckedChange={(v) => setDraft((d) => ({ ...d, showWrittenOff: v }))}
                    />
                  </div>
                </>
              )}

            </div>
          </ScrollAreaViewport>
          <ScrollAreaScrollbar orientation="vertical" />
        </ScrollArea>

        {/* Footer */}
        <SheetFooter className="px-6 py-4 border-t flex-row gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="gap-2 text-muted-foreground"
            onClick={handleClear}
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Clear all
          </Button>
          <Button className="flex-1" onClick={handleApply}>
            Apply filters{activeCount > 0 ? ` (${activeCount})` : ''}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
